// Helpers for the Self-Hosted licenses / plugins / downloads portal (WS7).
// Every endpoint here is Private: send the JWT in the x-auth-token header.
// These call the API host (host-api.northcommerce.com in prod) via axiosInstance,
// whose baseURL already includes /api/v1/.
import axiosInstance from './axiosConfig';
import { authHeaders, parseApiError } from './billingApi';

export { authHeaders, parseApiError };

// GET /licenses -> array of the caller's licenses (+ activations + entitlements).
export const getLicenses = async () => {
  const { data } = await axiosInstance.get('licenses', authHeaders());
  return Array.isArray(data) ? data : [];
};

// DELETE /licenses/:key/domains/:domain -> release an active site/domain.
// The domain must be URL-encoded; the backend decodes it.
export const releaseDomain = async (licenseKey, domain) => {
  const { data } = await axiosInstance.delete(
    `licenses/${encodeURIComponent(licenseKey)}/domains/${encodeURIComponent(domain)}`,
    authHeaders(),
  );
  return data;
};

// GET /plugins -> catalog the caller can download (active-license gated).
// Empty array means the user has no active license.
export const getPlugins = async () => {
  const { data } = await axiosInstance.get('plugins', authHeaders());
  return Array.isArray(data) ? data : [];
};

// GET /plugins/:slug/versions -> every version + changelog + download endpoint.
export const getPluginVersions = async (slug) => {
  const { data } = await axiosInstance.get(
    `plugins/${encodeURIComponent(slug)}/versions`,
    authHeaders(),
  );
  return Array.isArray(data) ? data : [];
};

// The API origin (scheme + host), WITHOUT the /api/v1 path. The version objects'
// `download_endpoint` already starts with /api/v1, so we prefix the origin only —
// prefixing the full baseURL would double the /api/v1 segment.
const apiOrigin = () => {
  const base = axiosInstance.defaults.baseURL || '';
  try {
    return new URL(base).origin;
  } catch {
    return '';
  }
};

// GET /plugins/:slug/versions/:version/download
// The endpoint 302-redirects to a short-lived signed Storage URL, but it needs the
// x-auth-token header — so a bare <a href> won't work. We fetch it (fetch follows
// the 302 automatically), read the resulting blob, and trigger a browser save.
// `downloadEndpoint` is the prebuilt relative path from getPluginVersions(); never
// cache the resolved signed URL (it expires in ~120s) — always fetch fresh.
export const downloadPluginVersion = async (downloadEndpoint, fileName) => {
  const url = `${apiOrigin()}${downloadEndpoint}`;
  const response = await fetch(url, {
    headers: { 'x-auth-token': localStorage.getItem('CloudForge-Token') },
  });

  if (!response.ok) {
    // Errors (403 not entitled, 404 unknown) come back as JSON before the redirect.
    let message = `Download failed (${response.status}).`;
    try {
      const body = await response.json();
      message = body.msg || body.error || message;
    } catch {
      /* non-JSON body; keep the generic message */
    }
    const error = new Error(message);
    error.status = response.status;
    throw error;
  }

  const blob = await response.blob();
  const objectUrl = window.URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = fileName || 'plugin.zip';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.URL.revokeObjectURL(objectUrl);
};
